import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { PRODUCT_NAME } from "./admin-nav";
import { errorMessage, tierName } from "./first-round";
import { type ConfirmOptions, useConfirm } from "./ui/ConfirmDialog";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader } from "./ui/PageHeader";

export function FeatureDetails() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [range, setRange] = useState<{ start: string; end: string }>(() => {
    const now = new Date();
    const end = now.toISOString();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return { start, end };
  });
  const { data: feature, isLoading, error } = useQuery({ queryKey: ["feature", id], queryFn: () => adminApi.getFeature(id!) });
  const { data: analytics } = useQuery({ queryKey: ["feature-analytics", id, range], queryFn: () => adminApi.getFeatureAnalytics(id!, range.start, range.end) });

  const [limitError, setLimitError] = useState<string | null>(null);
  const updateLimit = useMutation({
    mutationFn: (payload: { tier: string; monthlyLimit: number | null }) => adminApi.updateTierLimit(id!, payload.tier, { monthlyLimit: payload.monthlyLimit }),
    onSuccess: () => {
      setLimitError(null);
      qc.invalidateQueries({ queryKey: ["feature", id] });
      toast({ title: "Limit saved", variant: "success" });
    },
    onError: (e) => setLimitError(errorMessage(e)),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">Couldn't load this feature: {errorMessage(error)}</p>;
  if (!feature) return <p className="text-sm text-slate-500">Not found</p>;

  const columns: Column<any>[] = [
    { key: "plan", header: "Plan", cell: (t) => <span className="font-medium">{t.label ?? t.tier}</span> },
    {
      key: "limit",
      header: "Limit",
      cell: (t) =>
        t.missing ? <span className="text-slate-500">Not set (unlimited)</span> : t.monthlyLimit ?? "Unlimited",
    },
    { key: "shown", header: "Shown as", cell: (t) => <span className="text-slate-600">{t.displayText ?? "—"}</span> },
    {
      key: "update",
      header: "Update",
      cell: (t) => (
        <InlineLimitEditor
          tier={t.label ?? t.tier}
          disabled={updateLimit.isPending}
          confirm={confirm}
          onSave={(val) => updateLimit.mutate({ tier: t.tier, monthlyLimit: val })}
        />
      ),
    },
  ];

  return (
    <div className="max-w-5xl space-y-8">
      <PageHeader
        back={{ to: "/admin/features", label: "Features & limits" }}
        title={
          <span className="flex items-center gap-2">
            {feature.name}
            <Badge variant="outline" className="font-sans text-xs font-normal normal-case tracking-normal">
              {PRODUCT_NAME[feature.product as keyof typeof PRODUCT_NAME] ?? feature.product}
            </Badge>
          </span>
        }
        description={<code className="font-mono text-xs">{feature.key}</code>}
      />
      <section>
        <h2 className="mb-1 font-medium">Limits per plan</h2>
        {feature.product === "THERESUME" && (
          <p className="mb-3 text-sm text-slate-600">
            First Round plans: Free, Starter and Active. Practice interviews on Starter and Active count for the whole pass; the
            text next to each number is what the pricing cards say. Changes reach theResume straight away.
          </p>
        )}
        <DataTable label="Limits per plan" columns={columns} rows={feature.tiers} rowKey={(t) => t.tier} pageSize={null} />
        {limitError && <p className="mt-2 text-sm text-red-600">{limitError}</p>}
      </section>
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-medium">Usage</h2>
          <div className="flex items-center gap-2 text-sm">
            <Input className="w-auto" type="datetime-local" aria-label="From" value={toLocalDT(range.start)} onChange={(e) => setRange((r) => ({ ...r, start: new Date(e.target.value).toISOString() }))} />
            <span className="text-slate-500">to</span>
            <Input className="w-auto" type="datetime-local" aria-label="To" value={toLocalDT(range.end)} onChange={(e) => setRange((r) => ({ ...r, end: new Date(e.target.value).toISOString() }))} />
          </div>
        </div>
        {analytics ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat label="Uses" value={analytics.totalEvents ?? 0} />
              <Stat label="Success rate" value={`${Math.round((analytics.successRate ?? 0) * 100)}%`} />
              <Stat label="Distinct users" value={analytics.uniqueUsers ?? 0} />
            </div>
            {analytics.tierDistribution?.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="mb-2 text-sm font-medium">Usage counted against each plan since the start date</div>
                  <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-3">
                    {analytics.tierDistribution.map((row: any) => (
                      <div key={row.tier} className="flex justify-between border-b border-slate-100 pb-1 text-sm">
                        <dt className="text-slate-600">{tierName(feature.product, row.tier)}</dt>
                        <dd className="font-medium tabular-nums">{row._sum?.usageCount ?? 0}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
      </section>
      {confirmDialog}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function InlineLimitEditor({
  tier,
  disabled,
  confirm,
  onSave,
}: {
  tier: string;
  disabled?: boolean;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  onSave: (v: number | null) => void;
}) {
  const [val, setVal] = useState<string>("");
  const valid = /^\d+$/.test(val.trim());
  return (
    <div className="flex items-center gap-2">
      <Input
        className="h-8 w-24"
        type="number"
        min={0}
        step={1}
        placeholder="e.g. 3"
        aria-label={`New limit for ${tier}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
      />
      <Button
        size="sm"
        variant="outline"
        disabled={!valid || disabled}
        onClick={() => {
          onSave(Number(val.trim()));
          setVal("");
        }}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        onClick={async () => {
          if (await confirm({ title: `Make this unlimited on ${tier}?`, confirmLabel: "Make unlimited" })) onSave(null);
        }}
      >
        Unlimited
      </Button>
    </div>
  );
}

function toLocalDT(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
